import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeComparePosts.graphql';
import { Query, QueryyoutubeComparePostsArgs } from '../../__generated__/globalTypes';

export const useYoutubeComparePostsQuery = (options?: QueryHookOptions<Query, QueryyoutubeComparePostsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeComparePostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeComparePostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeComparePostsArgs>
): QueryResult<Query, QueryyoutubeComparePostsArgs> & {
  getYoutubeComparePosts: LazyQueryExecFunction<Query, QueryyoutubeComparePostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeComparePosts, result] = useLazyQuery<Query, QueryyoutubeComparePostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeComparePosts, ...result };
};

export const useYoutubeComparePostsPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeComparePosts = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeComparePostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeComparePostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeComparePosts };
};
// Suspense Query Hooks
export const useYoutubeComparePostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeComparePostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeComparePostsArgs>(QUERY, {
    ...options
  });

  return result;
};
